import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';

const REACT_APP_API_URL = `${process.env.REACT_APP_API_URL}`;

export interface ICompanyImage {
  id: number;
  companyId: number;
  imageUrl: string;
  altText: string;
  isMain: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt?: string;
}

export interface ICompanyImagesState {
  images: ICompanyImage[];
  loading: boolean;
  error?: string;
}

const initialState: ICompanyImagesState = {
  images: [],
  loading: false,
  error: undefined,
};

// Async Thunks

/**
 * Get all company images
 */
export const fetchCompanyImages = createAsyncThunk('companyImages/fetchAll', async () => {
  const res = await axios.get<ICompanyImage[]>(`${REACT_APP_API_URL}/company-images`);
  return res.data;
});

/**
 * Create a single company image
 */
export const createCompanyImage = createAsyncThunk('companyImages/create', async (data: { imageUrl: string; altText?: string; isMain?: boolean }) => {
  const res = await axios.post<ICompanyImage>(`${REACT_APP_API_URL}/company-images`, data);
  return res.data;
});

/**
 * Upload a single image file
 */
export const uploadCompanyImage = createAsyncThunk('companyImages/upload', async (data: { file: File; altText?: string; isMain?: boolean }) => {
  const formData = new FormData();
  formData.append('image', data.file);
  if (data.altText) {
    formData.append('altText', data.altText);
  }
  if (data.isMain !== undefined) {
    formData.append('isMain', String(data.isMain));
  }
  const res = await axios.post<{ success: boolean; data: ICompanyImage; imageUrl: string }>(`${REACT_APP_API_URL}/company-images/upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return res.data.data;
});

/**
 * Upload multiple image files
 */
export const uploadMultipleCompanyImages = createAsyncThunk('companyImages/uploadMultiple', async (files: File[], { dispatch }) => {
  const uploadedImages: ICompanyImage[] = [];
  for (const file of files) {
    const result = await dispatch(uploadCompanyImage({ file, altText: file.name })).unwrap();
    uploadedImages.push(result);
  }
  return uploadedImages;
});

/**
 * Bulk upload company images
 */
export const bulkUploadCompanyImages = createAsyncThunk('companyImages/bulkUpload', async (images: Array<{ imageUrl: string; altText?: string }>) => {
  const res = await axios.post<ICompanyImage[]>(`${REACT_APP_API_URL}/company-images/bulk-upload`, { images });
  return res.data;
});

/**
 * Update company image (alt text)
 */
export const updateCompanyImage = createAsyncThunk('companyImages/update', async ({ imageId, altText }: { imageId: number; altText: string }) => {
  const res = await axios.patch<ICompanyImage>(`${REACT_APP_API_URL}/company-images/${imageId}`, { altText });
  return res.data;
});

/**
 * Delete company image
 */
export const deleteCompanyImage = createAsyncThunk('companyImages/delete', async (imageId: number) => {
  await axios.delete(`${REACT_APP_API_URL}/company-images/${imageId}`);
  return imageId;
});

/**
 * Set image as main
 */
export const setMainCompanyImage = createAsyncThunk('companyImages/setMain', async (imageId: number) => {
  const res = await axios.patch<ICompanyImage>(`${REACT_APP_API_URL}/company-images/${imageId}/set-main`);
  return res.data;
});

/**
 * Reorder images
 */
export const reorderCompanyImages = createAsyncThunk('companyImages/reorder', async (imageOrders: Array<{ id: number; displayOrder: number }>) => {
  const res = await axios.patch<ICompanyImage[]>(`${REACT_APP_API_URL}/company-images/reorder`, { imageOrders });
  return res.data;
});

// Slice
const companyImagesSlice = createSlice({
  name: 'companyImages',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = undefined;
    },
    optimisticReorder: (state, action: PayloadAction<ICompanyImage[]>) => {
      state.images = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch all images
    builder
      .addCase(fetchCompanyImages.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchCompanyImages.fulfilled, (state, action) => {
        state.loading = false;
        state.images = action.payload;
      })
      .addCase(fetchCompanyImages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });

    // Create image
    builder
      .addCase(createCompanyImage.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(createCompanyImage.fulfilled, (state, action) => {
        state.loading = false;
        state.images.push(action.payload);
      })
      .addCase(createCompanyImage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });

    // Bulk upload
    builder
      .addCase(bulkUploadCompanyImages.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(bulkUploadCompanyImages.fulfilled, (state, action) => {
        state.loading = false;
        state.images.push(...action.payload);
      })
      .addCase(bulkUploadCompanyImages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });

    // Upload single image file
    builder
      .addCase(uploadCompanyImage.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(uploadCompanyImage.fulfilled, (state, action) => {
        state.loading = false;
        state.images.push(action.payload);
      })
      .addCase(uploadCompanyImage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });

    // Upload multiple image files
    builder
      .addCase(uploadMultipleCompanyImages.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(uploadMultipleCompanyImages.fulfilled, (state, action) => {
        state.loading = false;
        // Images are already added by individual upload calls
      })
      .addCase(uploadMultipleCompanyImages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });

    // Update image
    builder
      .addCase(updateCompanyImage.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(updateCompanyImage.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.images.findIndex((img) => img.id === action.payload.id);
        if (index !== -1) {
          state.images[index] = action.payload;
        }
      })
      .addCase(updateCompanyImage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });

    // Delete image
    builder
      .addCase(deleteCompanyImage.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(deleteCompanyImage.fulfilled, (state, action) => {
        state.loading = false;
        state.images = state.images.filter((img) => img.id !== action.payload);
      })
      .addCase(deleteCompanyImage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });

    // Set main image
    builder
      .addCase(setMainCompanyImage.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(setMainCompanyImage.fulfilled, (state, action) => {
        state.loading = false;
        // Update all images: set isMain to false except for the updated one
        state.images = state.images.map((img) => ({
          ...img,
          isMain: img.id === action.payload.id,
        }));
      })
      .addCase(setMainCompanyImage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });

    // Reorder images
    builder
      .addCase(reorderCompanyImages.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(reorderCompanyImages.fulfilled, (state, action) => {
        state.loading = false;
        state.images = action.payload;
      })
      .addCase(reorderCompanyImages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const { clearError, optimisticReorder } = companyImagesSlice.actions;
export default companyImagesSlice.reducer;
